import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  GAME,
  SIGNAL,
  callsignAt,
  eveningFor,
  leaderboard,
  gamesOf,
  pointsFor,
  professionCapacity,
  professionOptionsFor,
  stopOf,
  taskDef,
  taskKey,
  watcherCount,
} from '@continuum/show-core';
import type { Alignment, Chapter, Profession } from '@continuum/show-core';
import type { You } from './room/client.js';
import { useRoom } from './room/useRoom.js';
import type { Room } from './room/useRoom.js';
import { carrySeatKeys, clearSession, readLang, resolveSession, saveSession, writeLang } from './room/session.js';
import type { Session } from './room/session.js';
import { CheckedOut } from './screens/CheckedOut.js';
import { NoPass, Notice } from './screens/NoPass.js';
import { SignalBillboard, SignalCall, SignalReminder, signalCallDue, signalCallKey, signalReminderDue, signalResultFor, takeSignal } from './screens/Signal.js';
import type { SignalResult } from './screens/Signal.js';
import { Shell } from './ui/Shell.js';
import { COPY } from './ui/text.js';
import type { Lang } from './ui/text.js';
import { postName, screenText } from './ui/screenText.js';
import type { ScreenCopy } from './ui/screenText.js';
import { SCORE_COPY, fmt } from './ui/scoreText.js';
import { routeRoomError } from './ui/errorText.js';
import { OverlayProvider, PRIORITY, useBanner, useCoverCount } from './ui/OverlayHost.js';
import { TopBanner } from './ui/TopBanner.js';
import { Station } from './games/Station.js';
import { Hack } from './games/Hack.js';
import { ChargedSheet, DrawSheet, PlayNowSheet, RevealSheet, RoleStep, TalkSheet, VerdictSheet, drawAsked } from './screens/Round.js';
import { armedOf, saboteurAtOf } from './ui/you.js';
import { ROUND_COPY } from './ui/roundText.js';
import { clockKey } from './games/clock.js';
import { BoardCard } from './ui/BoardCard.js';
import { Wrapped } from './screens/Wrapped.js';
import { chatCounts, wrappedStats } from './ui/wrappedStats.js';
import type { ChatSnapshot } from './chat/store.js';
import { buildQueue, withSabotage } from './ui/TaskQueue.js';
import type { QueueRow } from './ui/TaskQueue.js';
import { LangSwitch, ProfessionPick, Welcome } from './screens/Boarding.js';
import { GameLayer, GameToolbar, HowToPlay, RoleSheet, VolunteerPick } from './screens/Game.js';
import { GameScreen } from './screens/GameScreen.js';
import { FitToScreen } from './ui/FitToScreen.js';
import { TableGame } from './screens/TableGameCard.js';
import { Serve } from './screens/Serve.js';
import { CourseCard, PhotoCall } from './screens/Interlude.js';
import { CatchUp } from './screens/CatchUp.js';
import { Jump } from './screens/Jump.js';
import { Record, Clock, Farewell } from './screens/Chronicle.js';
import { Earth } from './screens/fieldwork/Earth.js';
import { PerfectCity } from './screens/fieldwork/PerfectCity.js';
import { War } from './screens/fieldwork/War.js';
import { Underground } from './screens/fieldwork/Underground.js';
import { Surface } from './screens/fieldwork/Surface.js';
import { ChatCorner } from './chat/ChatCorner.js';
import { useSimAutopilot } from './sim/seam.js';

type BoardingStep = 'welcome' | 'profession' | 'volunteer' | 'rules' | 'seated';

/** A coaster's hidden signal, read off the address once as the page opens (it is taken off it at once). */
const SIGNAL_AT_START = typeof window === 'undefined' ? null : takeSignal();

interface Toast {
  id: number;
  text: string;
  ms: number;
}

/**
 * The gate: which phone is this, and may it be here at all. Everything that needs a live room
 * is in `Voyage` below, so this part never changes its hooks between one screen and the next.
 */
export function App() {
  const [session, setSession] = useState<Session | null>(() => resolveSession());
  const [lang, setLang] = useState<Lang>(() => readLang() ?? 'en');
  const [checkedOut, setCheckedOut] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [privateNote, setPrivateNote] = useState<{ text: string; at: number } | null>(null);
  const [rejections, setRejections] = useState(0);
  // A coaster's hidden signal, if this page was opened from one (screens/Signal.tsx).
  const [signal, setSignal] = useState<{ code: string; result: SignalResult } | null>(() =>
    SIGNAL_AT_START ? { code: SIGNAL_AT_START, result: 'tuning' } : null,
  );
  const langRef = useRef(lang);
  langRef.current = lang;
  const seq = useRef(0);

  const toast = useCallback((text: string, ms = 4_500) => {
    seq.current += 1;
    const id = seq.current;
    // A short queue: the newest few, one on screen at a time.
    setToasts((list) => [...list.filter((t) => t.text !== text).slice(-3), { id, text, ms }]);
  }, []);
  const dropToast = useCallback((id: number) => setToasts((list) => list.filter((t) => t.id !== id)), []);

  const room = useRoom(session, {
    // The captain swapped this guest: the new seat's pass becomes this phone's own, and it rejoins there.
    moved: (next) => {
      // Everything this phone did under the old seat comes along: no role card or signal call again.
      if (session && session.voyageId === next.voyageId) carrySeatKeys(next.voyageId, session.seat, next.seat);
      saveSession(next);
      setSession(next);
      toast(screenText(langRef.current).moved(String(next.seat).padStart(2, '0')), 7_000);
    },
    // The captain checked this guest out: the pass is gone, and so is this phone's seat.
    freed: () => {
      clearSession();
      setCheckedOut(true);
    },
    rejected: (error, intent) => {
      // The signal's own answer goes on its billboard, never in a toast.
      if (intent === 'signalFound') {
        setSignal((s) => (s ? { ...s, result: signalResultFor(error) } : s));
        return;
      }
      setRejections((n) => n + 1);
      const route = routeRoomError(error, intent, langRef.current);
      if (route.kind === 'private') setPrivateNote({ text: route.text, at: Date.now() });
      else if (route.kind === 'toast') toast(route.text);
    },
  });
  // Sim seam: in the sim bench's phones an autopilot can play this one (src/sim/); in any other build, nothing.
  useSimAutopilot(session, room, lang);

  // The signal goes to the room once the seat is in; the phone's own view says when it counted.
  const signalSent = useRef(false);
  useEffect(() => {
    if (!signal || signal.result !== 'tuning' || !room.you || room.status !== 'open' || signalSent.current) return;
    signalSent.current = true;
    if (room.you.signal) setSignal({ ...signal, result: 'already' });
    else room.send({ type: 'signalFound', code: signal.code });
  }, [signal, room.you, room.status]);
  useEffect(() => {
    if (signalSent.current && room.you?.signal) setSignal((s) => (s && s.result === 'tuning' ? { ...s, result: 'found' } : s));
  }, [room.you?.signal]);

  const onLang = useCallback((l: Lang) => {
    setLang(l);
    writeLang(l);
  }, []);

  if (checkedOut) return <CheckedOut lang={lang} />;
  // A coaster scanned on a phone with no seat here: the billboard as a teaser for the show.
  if (!session && signal) return <SignalBillboard lang={lang} result="teaser" />;
  if (!session) return <NoPass lang={lang} />;
  return (
    <OverlayProvider>
      {signal ? <SignalBillboard lang={lang} result={signal.result} onClose={() => setSignal(null)} /> : null}
      <Voyage
        session={session}
        room={room}
        lang={lang}
        onLang={onLang}
        toasts={toasts}
        toast={toast}
        dropToast={dropToast}
        privateNote={privateNote}
        rejections={rejections}
        signalOpen={signal !== null}
      />
    </OverlayProvider>
  );
}

/** Remember how far this phone saw the evening get, so a shut door can be told apart from an ended one. */
function lastPhaseKey(voyageId: string): string {
  return `continuum.phase.${voyageId}`;
}

function Voyage({
  session,
  room,
  lang,
  onLang,
  toasts,
  toast,
  dropToast,
  privateNote,
  rejections,
  signalOpen,
}: {
  session: Session;
  room: Room;
  lang: Lang;
  onLang: (l: Lang) => void;
  toasts: Toast[];
  toast: (text: string, ms?: number) => void;
  dropToast: (id: number) => void;
  privateNote: { text: string; at: number } | null;
  rejections: number;
  /** A coaster's billboard is on screen (App): nothing about the signal comes up over it. */
  signalOpen: boolean;
}) {
  const t = COPY[lang];
  const s = screenText(lang);
  const sc = SCORE_COPY[lang];
  const { state, you, send, charge, status, refused } = room;

  // Local check-in progress; the room holds the result, this holds the wizard.
  const [step, setStep] = useState<BoardingStep>('welcome');
  const [draftProfession, setDraftProfession] = useState<Profession | null>(null);
  const [pending, setPending] = useState(false);
  /**
   * Whether this seat still has to check in. Latched the first time the room tells us who this
   * seat is, and never recomputed: deriving it live would drop the guest out of the wizard the
   * instant they picked a job, skipping the saboteur question. Latched during render rather than
   * in an effect so a returning phone never flashes the welcome screen on its way back.
   */
  const checkIn = useRef<boolean | null>(null);

  // Screen-local play state.
  const [openTask, setOpenTask] = useState<{ index: number; def: NonNullable<ReturnType<typeof taskDef>> } | null>(null);
  const [watching, setWatching] = useState<number | null>(null);
  const [chronicleStep, setChronicleStep] = useState<'record' | 'clock' | 'farewell'>('record');
  // The end-of-era leaderboard, once pulled down, waits behind SCORES on the toolbar.
  const [boardAside, setBoardAside] = useState<Partial<Record<number, true>>>({});
  const [wrappedOpen, setWrappedOpen] = useState(false);
  const [controlsOpen, setControlsOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  // The round (screens/Round.tsx): the saboteur's own game open, and the round's cards put away, by moment.
  const [hackOpen, setHackOpen] = useState(false);
  const [roundAside, setRoundAside] = useState<Record<string, true>>({});
  const putRound = useCallback((key: string) => setRoundAside((a) => (a[key] ? a : { ...a, [key]: true })), []);

  // The role card shows by itself once per seat per draw, at the era's role step; after that, from the step's
  // own button or CONTROLS → MY ROLE. Kept per evening: a rehearsal's draw is never taken for tonight's.
  const draws = state?.game?.draws ?? 0;
  const roleEvening = state ? `${state.date}.${state.launchedAt ?? 0}` : '';
  const roleKey = `continuum.role.${session.voyageId}.${session.seat}.${roleEvening}.${draws}`;
  const [roleSeen, setRoleSeen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(roleKey) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      setRoleSeen(localStorage.getItem(roleKey) === '1');
    } catch {
      setRoleSeen(false);
    }
  }, [roleKey]);
  const markRoleSeen = useCallback(() => {
    setRoleSeen(true);
    setRoleOpen(false);
    try {
      localStorage.setItem(roleKey, '1');
    } catch {
      /* private browsing: it simply shows again next time */
    }
  }, [roleKey]);

  // Advance only on the room's word. If it refuses, we stay put and say so.
  useEffect(() => {
    if (you?.callsign && you.profession) {
      setPending(false);
      // Everyone is asked, before launch or after it: a late yes counts when the captain draws again.
      if (step === 'profession') setStep('volunteer');
    }
  }, [you, step, state?.game?.drawn]);

  useEffect(() => {
    if (rejections > 0) setPending(false);
  }, [rejections]);

  // A tap the room never answered (the line dropped for good) must not leave the button waiting for ever.
  useEffect(() => {
    if (!pending) return;
    const id = setTimeout(() => setPending(false), 10_000);
    return () => clearTimeout(id);
  }, [pending]);

  const chapter: Chapter = state?.chapter ?? 0;
  const phase = state?.phase ?? 'boarding';

  // How far the evening got, for the door: after docking a shut door means the show is over.
  useEffect(() => {
    if (!state) return;
    try {
      localStorage.setItem(lastPhaseKey(session.voyageId), state.access.sealed ? 'sealed' : phase);
    } catch {
      /* nothing kept; the door screen then says "not open yet" */
    }
  }, [state, phase, session.voyageId]);

  /**
   * A game is only valid for the era it was opened in. When the captain jumps, an open game
   * would keep playing the old era's one, showing the wrong words and sending taps the room
   * refuses. Close it and let the guest open the new era's.
   */
  useEffect(() => {
    setOpenTask(null);
    setWatching(null);
    setHackOpen(false);
  }, [chapter]);

  /**
   * If the room says this seat holds no job while we believe we are checked in, the manifest was
   * wiped underneath us — a reset does exactly that. Send them back through check-in.
   */
  useEffect(() => {
    if (step === 'seated' && you && !you.profession) {
      checkIn.current = true;
      setStep('welcome');
    }
  }, [step, you]);

  // Lost the line for more than a moment: say so, once, at the top.
  const [offline, setOffline] = useState(false);
  useEffect(() => {
    if (status === 'open') {
      setOffline(false);
      return;
    }
    const id = setTimeout(() => setOffline(true), 3_000);
    return () => clearTimeout(id);
  }, [status]);

  // Anything covering the screen (a sheet, the alarm card) or a meeting: the open game waits.
  const covered = useCoverCount();

  const seatLabel = `${t.seat} ${String(session.seat).padStart(2, '0')}`;
  // Only what the room has confirmed; until then, the seat alone.
  const callsign = you?.callsign ?? '';
  // A guest the host added at the door may already hold a post: they still answer the saboteur
  // question and see the rules, and only the choice of post is skipped.
  if (checkIn.current === null && you) checkIn.current = !you.profession || you.volunteer === null;
  const mustCheckIn = checkIn.current !== false && step !== 'seated';

  const title = useCallback((def: NonNullable<ReturnType<typeof taskDef>>) => s.taskTitle(chapter, def), [s, chapter]);

  // The hidden signal's call and its reminder (screens/Signal.tsx), kept per evening: the room is always the
  // same voyage, and "Start a new evening" renews the date or the launch.
  const evening = state ? `${state.date}.${state.launchedAt ?? 0}` : '';
  const [signalCallSeen, markSignalCall] = useStoredFlag(signalCallKey('call', session.voyageId, session.seat, evening));
  const [signalReminderSeen, markSignalReminder] = useStoredFlag(signalCallKey('reminder', session.voyageId, session.seat, evening));
  const [signalCallOpen, setSignalCallOpen] = useState(false);

  // A guest who boarded late (2026-10-24): once their check-in is done mid-evening, the stops they missed
  // (screens/CatchUp.tsx), then their role and, in 2213, the hidden signal's call, as for everyone else.
  const [lateJoin, setLateJoin] = useState(false);
  const [catchUpSeen, markCatchUp] = useStoredFlag(`continuum.catchup.${session.voyageId}.${session.seat}.${evening}`);

  // This phone's own vote in each of the stops' meetings, kept on the phone for the reveal's points: the
  // room never sends a ballot back once the vote is counted (show-core wire.ts).
  const ballotsKey = `continuum.ballots.${session.voyageId}.${session.seat}.${evening}`;
  const [ballots, setBallots] = useState<Record<string, string>>({});
  useEffect(() => {
    try {
      setBallots(JSON.parse(localStorage.getItem(ballotsKey) ?? '{}') as Record<string, string>);
    } catch {
      setBallots({});
    }
  }, [ballotsKey]);
  const openMeeting = state?.game.meeting ?? null;
  useEffect(() => {
    if (!openMeeting?.pick || !you?.ballot) return;
    const id = openMeeting.id;
    const ballot = you.ballot;
    setBallots((had) => {
      if (had[id] === ballot) return had;
      const next = { ...had, [id]: ballot };
      try {
        localStorage.setItem(ballotsKey, JSON.stringify(next));
      } catch {
        /* kept for this visit only */
      }
      return next;
    });
  }, [openMeeting?.id, openMeeting?.pick, you?.ballot, ballotsKey]);

  // The clicker's last step (2026-10-09): every phone opens its own evening, wrapped, once.
  const wrappedAt = state?.wrappedAt ?? null;
  const wrappedShown = useRef<number | null>(null);
  useEffect(() => {
    if (!wrappedAt || wrappedShown.current === wrappedAt || state?.phase !== 'farewell' || mustCheckIn) return;
    wrappedShown.current = wrappedAt;
    setWrappedOpen(true);
  }, [wrappedAt, state?.phase, mustCheckIn]);

  // ---------------------------------------------------------------- the door said no
  if (!state && refused) {
    let ended = false;
    try {
      const seen = localStorage.getItem(lastPhaseKey(session.voyageId));
      ended = seen === 'farewell' || seen === 'sealed';
    } catch {
      /* unknown: say the door is not open yet */
    }
    if (refused === 'replaced') return <Notice title={s.refusedReplacedTitle} lines={[s.refusedReplaced]} />;
    if (refused === 'expired') return <Notice title={s.refusedReplacedTitle} lines={[s.refusedExpired]} />;
    if (ended) return <Notice title={s.refusedEndedTitle} lines={[s.refusedEnded]} />;
    return (
      <Notice
        title={s.refusedNotOpenTitle}
        lines={[s.refusedNotOpen]}
        action={{ label: s.tryAgain, onClick: () => window.location.reload() }}
      />
    );
  }

  // Every game dealt to this seat this era: its own post's, and any it stands in for (show-core `dealGames`).
  const myGames = state && chapter > 0 ? gamesOf(state, chapter, session.seat) : [];
  const myTaskDone = state ? myGames.every((i) => state.tasks[taskKey(chapter, i, session.seat)]?.done ?? false) : false;
  const ownsOpenTask = openTask !== null && myGames.includes(openTask.index) && !watching;
  const dealtRows: QueueRow[] = state && chapter > 0 ? buildQueue(chapter, state.seats, state.tasks, session.seat, state.gameSet) : [];
  // A saboteur's game of the stop is their sabotage (Marek, 2026-10-06; games/Hack.tsx): it takes the place of
  // the games dealt to them, under an ordinary name, and the room finishes those once it is solved.
  const r = ROUND_COPY[lang];
  const stop = state ? stopOf(state) : null;
  const firstMine = dealtRows.findIndex((row) => row.mine);
  const armed = armedOf(you, state);
  const hackRow: QueueRow | null =
    you?.alignment === 'PARADOX AGENT' && !you.brig && phase === 'fieldwork' && dealtRows[0]
      ? {
          ...(dealtRows[firstMine] ?? dealtRows[0]),
          index: -1,
          mine: true,
          extra: false,
          done: armed || stop !== null,
          progress: armed ? 100 : 0,
          owner: null,
          ownerPost: null,
          ownerSeat: null,
          hack: r.hackTitle,
        }
      : null;
  const rows: QueueRow[] = withSabotage(dealtRows, hackRow);

  const watch = (target: number, index: number) => {
    const def = taskDef(chapter, index, state?.gameSet);
    if (!def) return;
    // The room only lets a guest watch once their own game is done; say so before trying.
    if (myGames.length > 0 && !myTaskDone) {
      toast(s.finishFirst);
      return;
    }
    setWatching(target);
    setOpenTask({ index, def });
    send({ type: 'watch', seat: target });
  };

  const open = (row: QueueRow) => {
    if (!state) return;
    if (row.hack) {
      setOpenTask(null);
      setHackOpen(true);
      return;
    }
    const def = taskDef(chapter, row.index, state.gameSet);
    if (!def) return;
    if (row.mine) {
      setWatching(null);
      send({ type: 'watch', seat: null });
      setOpenTask({ index: row.index, def });
      send({ type: 'taskProgress', chapter, index: row.index, progress: 1 });
      return;
    }
    // Somebody else's game: watch them, if your own is finished.
    if (row.ownerSeat !== null) watch(row.ownerSeat, row.index);
  };

  // An alarm needs this phone's FIX: the sheets a guest opened themselves step aside for it.
  const closeSheetsForAlarm = () => {
    setControlsOpen(false);
    if (roleSeen) setRoleOpen(false);
  };

  const closeGame = () => {
    if (watching) send({ type: 'watch', seat: null });
    setWatching(null);
    setOpenTask(null);
  };

  const body = () => {
    // ---- no room yet -----------------------------------------------------
    if (!state) return <Booting s={s} />;

    // ---- check-in --------------------------------------------------------
    // Check-in runs whenever this seat has no job yet, not only before launch: a guest who
    // scans an open seat's code mid-evening checks in exactly like everyone else.
    if (mustCheckIn) {
      if (step === 'welcome') {
        return (
          <Welcome
            t={t}
            s={s}
            lang={lang}
            seatLabel={seatLabel}
            crewSize={state.crewSize}
            onLang={onLang}
            onNext={() => setStep(you?.profession ? 'volunteer' : 'profession')}
          />
        );
      }
      if (step === 'profession') {
        // The seating rule lives in show-core: two guests with one job never sit close.
        const options = professionOptionsFor(state as never, session.seat);
        return (
          <ProfessionPick
            t={t}
            s={s}
            perJob={professionCapacity(state.crewSize)}
            chosen={draftProfession}
            options={options}
            onChoose={setDraftProfession}
            onNext={() => {
              if (!draftProfession) return;
              // The seat's call sign card; the room applies it whatever is sent.
              const name = callsignAt(state, session.seat) ?? '';
              send({ type: 'setIdentity', callsign: name, profession: draftProfession });
              setPending(true);
            }}
            pending={pending}
          />
        );
      }
      if (step === 'volunteer') {
        return (
          <VolunteerPick
            lang={lang}
            late={Boolean(state.game?.drawn)}
            chosen={you?.volunteer ?? null}
            onChoose={(willing) => send({ type: 'volunteer', willing })}
            onNext={() => setStep('rules')}
          />
        );
      }
      return (
        <HowToPlay
          lang={lang}
          onDone={() => {
            if (chapter >= 1) setLateJoin(true);
            setStep('seated');
          }}
        />
      );
    }

    // ---- boarded late: what they missed, once ------------------------------
    if (showCatchUp && chapter !== 0) return <CatchUp chapter={chapter} callsign={callsign} s={s} onDone={finishCatchUp} />;

    // ---- docked ----------------------------------------------------------
    if (phase === 'farewell') {
      if (chronicleStep === 'farewell') {
        return (
          <Farewell
            s={s}
            callsign={callsign}
            onBack={() => setChronicleStep('record')}
            wrapped={{ label: sc.wrappedOpen, sub: sc.wrappedOpenSub, onOpen: () => setWrappedOpen(true) }}
          />
        );
      }
      return chronicleStep === 'record' ? (
        <Record
          s={s}
          lang={lang}
          callsign={callsign}
          profession={you?.profession ?? null}
          alignment={eveningRole(you)}
          outcome={state.game.outcome}
          damage={state.game.fractures}
          uprising={state.game.uprising !== null}
          contributions={contributionsFor(state, session.seat, s)}
          onNext={() => setChronicleStep('clock')}
        />
      ) : (
        <Clock s={s} onHome={() => setChronicleStep('farewell')} />
      );
    }

    // ---- the jump --------------------------------------------------------
    if (phase === 'charge' || phase === 'jump') {
      return (
        <Jump chapter={chapter} phase={phase} s={s} />
      );
    }

    // ---- still boarding, already seated ----------------------------------
    if (chapter === 0) return <Seated t={t} s={s} seatNumber={String(session.seat).padStart(2, '0')} callsign={callsign} />;

    // ---- each era's role step: everyone holds to see theirs, at once --------
    if (phase === 'role') {
      return you?.alignment ? (
        <RoleStep lang={lang} seen={roleSeen} onOpen={() => setRoleOpen(true)} />
      ) : (
        <Serve chapter={chapter} t={t} s={s} />
      );
    }

    // ---- the drink (and 2213's hidden signal, called over it) ---------------
    if (phase === 'serve' || phase === 'intro' || phase === 'signal') return <Serve chapter={chapter} t={t} s={s} />;

    // ---- the plate -------------------------------------------------------
    if (phase === 'course' && (chapter === 1 || chapter === 3 || chapter === 5)) return <CourseCard chapter={chapter} s={s} />;

    // ---- the last picture on Earth ---------------------------------------
    // Era 1 only. Anywhere else the ship has already left, so fall through to the drink.
    if (phase === 'photo' && chapter === 1) return <PhotoCall startedAt={state.phaseStartedAt} s={s} />;
    if (phase === 'course' || phase === 'photo') return <Serve chapter={chapter} t={t} s={s} />;

    // ---- the era's games -------------------------------------------------
    switch (chapter) {
      case 1:
        return <Earth rows={rows} s={s} title={title} onOpen={open} />;
      case 2:
        return <PerfectCity rows={rows} s={s} title={title} onOpen={open} />;
      case 3:
        return <War rows={rows} s={s} title={title} onOpen={open} />;
      case 4:
        return <Underground rows={rows} s={s} title={title} onOpen={open} />;
      case 5:
        return <Surface rows={rows} s={s} title={title} onOpen={open} />;
      default:
        return <Seated t={t} s={s} seatNumber={String(session.seat).padStart(2, '0')} callsign={callsign} />;
    }
  };

  // ---------------------------------------------------------------- the round (screens/Round.tsx)
  // While the games are on: the call to play, once per opening of the games, until this guest has started
  // their own (a saboteur's is their sabotage). Once they are done the stop runs by itself, one step at a
  // time on every phone (show-core stop.ts): SHIP CHARGED, the alarms and the vote (GameLayer), the VERDICT,
  // the REVEAL, then before the jump the next draw and the scores. Each card can be put away; the next step
  // brings the next one.
  // A late guest's catch-up comes first: no card of the round over it until they join the crew.
  const showCatchUp = lateJoin && !catchUpSeen && !mustCheckIn && chapter >= 1 && phase !== 'jump' && phase !== 'intro' && phase !== 'farewell';
  const inGames = Boolean(state && you?.profession && !mustCheckIn && !showCatchUp && phase === 'fieldwork' && chapter > 0);
  const playKey = state ? `play:${chapter}:${state.phaseStartedAt}` : '';
  const myRow = rows.find((x) => x.mine && !x.done) ?? null;
  // A saboteur's sabotage shows as the first game dealt to them, so that is the one that says it started.
  const myIndex = myRow ? (myRow.hack ? (myGames[0] ?? null) : myRow.index) : null;
  const myStarted = myIndex !== null && Boolean(state?.tasks[taskKey(chapter, myIndex, session.seat)]);
  const showPlay = inGames && stop === null && myRow !== null && !myStarted && !openTask && !hackOpen && !roundAside[playKey];
  const stopStep = stop?.step ?? null;
  const stepKey = (name: string) => (stop ? `${name}:${chapter}:${stop.since}` : '');
  const showCharged = inGames && stopStep === 'charged' && !roundAside[stepKey('charged')];
  // The stop's meeting, before its vote: the table talks it over (2026-10-09).
  const showTalk = inGames && stopStep === 'meeting' && !state?.game.meeting && !roundAside[stepKey('talk')];
  // The stop's own vote, once counted, and its reveal.
  const stopMeeting = state ? ([...state.game.meetings].reverse().find((m) => m.chapter === chapter && m.pick && m.result) ?? null) : null;
  const reveal = state ? ((state.game.reveals ?? []).filter((x) => x.chapter === chapter).at(-1) ?? null) : null;
  const showVerdict = inGames && stopStep === 'verdict' && stopMeeting?.result != null && !roundAside[stepKey('verdict')];
  const showReveal = inGames && stopStep === 'reveal' && reveal !== null && !roundAside[stepKey('reveal')];
  // Before each jump, privately: in the next stop's saboteur draw, or not (Marek, 2026-10-01) — once a stop,
  // after its reveal and before the scores. A swipe away keeps the answer the guest had.
  const drawKey = `draw:${chapter}`;
  const drawDue = inGames && drawAsked(chapter, phase, stopStep === 'done');
  const showDraw = drawDue && !roundAside[drawKey] && !openTask && !hackOpen;
  const drawOver = !drawDue || Boolean(roundAside[drawKey]);

  // ---------------------------------------------------------------- what sits around the screen
  const gameActive = Boolean(state && you && state.game.drawn && chapter > 0 && phase !== 'farewell' && !mustCheckIn);
  const boardChapter = chapter as 1 | 2 | 3 | 4 | 5;
  const boardAvailable = Boolean(
    state &&
      you?.profession &&
      !mustCheckIn &&
      ((phase === 'charge' && chapter >= 1 && chapter <= 4) ||
        (phase === 'fieldwork' && chapter >= 1 && chapter <= 4 && stopStep === 'done' && drawOver) ||
        (phase === 'farewell' && chapter === 5)) &&
      state.seats.some((x) => pointsFor(state, x.seat, chapter) > 0),
  );
  const boardShown = boardAvailable && !boardAside[chapter] && !wrappedOpen && !showCatchUp;

  const toolbar =
    gameActive || (boardAvailable && boardAside[chapter]) ? (
      <>
        {gameActive && state && you && (
          <GameToolbar
            lang={lang}
            state={state}
            you={you}
            onControls={() => setControlsOpen(true)}
            onExplain={(text) => toast(text, 6_000)}
          />
        )}
        {boardAvailable && boardAside[chapter] && (
          <button
            onClick={() => setBoardAside((a) => ({ ...a, [chapter]: undefined }))}
            style={{
              minHeight: 44,
              padding: '0 14px',
              borderRadius: 22,
              border: '1px solid rgba(160,140,255,.6)',
              background: 'transparent',
              color: '#c9bdff',
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              letterSpacing: 1,
              marginLeft: gameActive ? 0 : 'auto',
            }}
          >
            {sc.scores} ›
          </button>
        )}
      </>
    ) : undefined;

  const accent = state ? lookAccent(chapter, phase) : '#4fd8c2';

  const tableGame = gameActive && state && you && state.tableGame && !state.game.meeting ? state.tableGame : null;
  const dock =
    tableGame ? (
      <>
        {tableGame && state && you && (
          <TableGame
            lang={lang}
            game={tableGame}
            state={state}
            you={you}
            send={send}
            gameOpen={openTask !== null}
            onOpenCard={closeGame}
          />
        )}
      </>
    ) : undefined;

  // A new stop's role comes up at its role step, right after the films (2026-10-09), and never over them; a
  // phone that slept through the step, or a draw made again later, brings it up as it wakes.
  const finishCatchUp = () => {
    markCatchUp();
    // In 2213 after its signal step, the call they missed comes next (behind the role card, as for everyone).
    const pastSignal = phase !== 'role' && phase !== 'serve' && phase !== 'signal';
    if (chapter === SIGNAL.chapter && pastSignal && !you?.signal && !signalCallSeen) setSignalCallOpen(true);
  };
  const showRole = Boolean(
    gameActive && you?.alignment && !showCatchUp && (roleOpen || (!roleSeen && phase !== 'jump' && phase !== 'intro')),
  );

  // ---------------------------------------------------------------- the hidden signal (screens/Signal.tsx)
  // On 2213's signal step (the clicker's), once an evening, every seated guest who has not found it is told a
  // signal is hidden near them, never where, and how to scan it; when the games open, a banner reminds them
  // once, while no game of theirs is open. Never over the role card or a coaster's own billboard.
  const signalMoment = { chapter, phase, seated: Boolean(state && you?.profession && !mustCheckIn), found: Boolean(you?.signal) };
  const signalEra = signalMoment.seated && !signalMoment.found && chapter === SIGNAL.chapter;
  const showSignalCall =
    !signalOpen && !showRole && ((signalCallOpen && signalEra) || signalCallDue(signalMoment, signalCallSeen));
  const showSignalReminder =
    !signalOpen && !showSignalCall && stop === null && !openTask && !hackOpen && signalReminderDue(signalMoment, signalReminderSeen);

  const gamePaused = covered > 0 || Boolean(state?.game.meeting);
  // No red line round a saboteur's screen any more (Marek, 2026-10-09: "too much giving away"): the role is
  // seen only while held, at the era's role step.
  const eraLook = s.look(chapter, phase);

  return (
    <Shell
      look={eraLook}
      callsign={callsign}
      seatLabel={seatLabel}
      charge={charge}
      chapter={chapter}
      phase={phase}
      stop={stopStep}
      lang={lang}
      onLang={onLang}
      toolbar={toolbar}
      dock={dock}
      // Messages to other guests, once this phone is checked in; never on the TV, table or cockpit.
      // A message's notice waits while a game is open: nothing interrupts the games.
      action={
        <ChatCorner
          room={room}
          seat={session.seat}
          lang={lang}
          enabled={Boolean(state && you?.profession && !mustCheckIn)}
          quiet={Boolean(openTask || hackOpen)}
        />
      }
    >
      {body()}

      {openTask && state && (
        <GameScreen
          accent={accent}
          eyebrow={watching ? s.gameWatching(nameOf(state, watching)) : s.gameMine}
          title={title(openTask.def)}
          instruction={s.instruction(openTask.def)}
          closeLabel={s.closeGame}
          onClose={closeGame}
        >
          <Station
            def={openTask.def}
            chapter={chapter}
            task={state.tasks[taskKey(chapter, openTask.index, watching ?? session.seat)]}
            accent={accent}
            observing={watching ? { seat: watching, callsign: nameOf(state, watching) } : null}
            watchers={watcherCount(state as never, session.seat)}
            onProgress={(progress) => {
              if (!ownsOpenTask) return;
              send({ type: 'taskProgress', chapter, index: openTask.index, progress });
            }}
            onFrame={(frame) => {
              if (!ownsOpenTask) return;
              send({ type: 'taskFrame', chapter, index: openTask.index, frame });
            }}
            // The TV follows this guest's own game while the room asks for it (show-core boardFeed.ts), never one watched.
            mirror={room.boardOn && ownsOpenTask}
            onMirror={room.sendBoard}
            onComplete={(run) => {
              if (!ownsOpenTask) return;
              send({ type: 'taskComplete', chapter, index: openTask.index, ...(run ?? {}) });
            }}
            clockKey={
              ownsOpenTask && !state.tasks[taskKey(chapter, openTask.index, session.seat)]?.done
                ? clockKey(session.voyageId, chapter, openTask.index, session.seat)
                : null
            }
            paused={gamePaused}
            lang={lang}
            s={s}
            nameOf={(seat) => state.seats.find((x) => x.seat === seat)?.callsign ?? null}
            onClose={closeGame}
            onStopWatching={closeGame}
          />
        </GameScreen>
      )}

      {/* The saboteur game, over whatever the show is doing — once launched, and never mid-check-in. */}
      {gameActive && state && you && (
        <GameLayer
          lang={lang}
          state={state}
          you={you}
          send={send}
          controlsOpen={controlsOpen}
          onCloseControls={() => setControlsOpen(false)}
          onShowRole={
            you.alignment
              ? () => {
                  setControlsOpen(false);
                  setRoleOpen(true);
                }
              : null
          }
          extra={<LangSwitch lang={lang} onLang={onLang} />}
          onUrgent={closeSheetsForAlarm}
        />
      )}
      {showRole && you?.alignment && (
        <RoleSheet lang={lang} alignment={you.alignment} chapter={chapter} fresh={!roleSeen} onDone={markRoleSeen} />
      )}

      {/* The saboteur's own game, full screen like any other. */}
      {hackOpen && state && you && (
        <GameScreen accent={accent} eyebrow={s.gameMine} title={r.hackTitle} instruction={r.hackInstruction} closeLabel={s.closeGame} onClose={() => setHackOpen(false)}>
          {/* One screen on a small phone too, scaled to fit rather than scrolled. */}
          <FitToScreen>
            <Hack lang={lang} state={state} you={you} send={send} privateNote={privateNote} paused={gamePaused} />
          </FitToScreen>
        </GameScreen>
      )}
      {showPlay && myRow && state && (
        <PlayNowSheet
          lang={lang}
          year={eraYear(chapter)}
          accent={accent}
          games={rows.filter((x) => x.mine && !x.done).map((x) => x.hack ?? title(x.def))}
          onPlay={() => open(myRow)}
          onDone={() => putRound(playKey)}
        />
      )}
      {showCharged && <ChargedSheet lang={lang} accent={accent} onDone={() => putRound(stepKey('charged'))} />}
      {showTalk && <TalkSheet lang={lang} step="meeting" onDone={() => putRound(stepKey('talk'))} />}
      {showVerdict && state && stopMeeting?.result && (
        <VerdictSheet
          lang={lang}
          result={stopMeeting.result}
          seat={session.seat}
          nameOf={(x) => nameOf(state, x)}
          onDone={() => putRound(stepKey('verdict'))}
        />
      )}
      {showReveal && state && reveal && (
        <RevealSheet
          lang={lang}
          reveal={reveal}
          out={stopMeeting?.result?.sentToBrig ?? null}
          seat={session.seat}
          ballot={stopMeeting ? (ballots[stopMeeting.id] ?? null) : null}
          nameOf={(x) => nameOf(state, x)}
          postOf={(x) => postName(state.seats.find((seat) => seat.seat === x)?.profession, lang)}
          onDone={() => putRound(stepKey('reveal'))}
        />
      )}
      {showDraw && (
        <DrawSheet
          lang={lang}
          willing={you?.volunteer ?? null}
          onChoose={(willing) => send({ type: 'volunteer', willing })}
          onDone={() => putRound(drawKey)}
        />
      )}
      {showSignalCall && (
        <SignalCall
          lang={lang}
          points={SIGNAL.points}
          onAway={() => {
            setSignalCallOpen(false);
            markSignalCall();
          }}
        />
      )}
      <SignalReminder
        lang={lang}
        show={showSignalReminder}
        points={SIGNAL.points}
        onOpen={() => {
          markSignalReminder();
          setSignalCallOpen(true);
        }}
        onAway={markSignalReminder}
      />


      {/* The end of an era: everyone's points for its game, on the charge, and at docking for the last. */}
      {boardShown && state && (
        <BoardCard
          lang={lang}
          state={state}
          chapter={boardChapter}
          seat={session.seat}
          accent={phase === 'farewell' ? '#d9b96a' : '#a08cff'}
          final={phase === 'farewell'}
          onAway={() => setBoardAside((a) => ({ ...a, [chapter]: true }))}
        />
      )}
      {state && phase === 'farewell' && wrappedOpen && (
        <Wrapped
          input={wrappedInput(state, session.seat, lang, you, room.chat.snapshot())}
          chronicle={{
            lang,
            callsign,
            profession: you?.profession ?? null,
            alignment: eveningRole(you),
            outcome: state.game.outcome,
            damage: state.game.fractures,
            uprising: state.game.uprising !== null,
            contributions: contributionsFor(state, session.seat, s),
          }}
          onClose={() => setWrappedOpen(false)}
        />
      )}

      {toasts.map((item) => (
        <ToastBanner key={item.id} item={item} onDone={dropToast} />
      ))}
      <ConnectionBanner
        text={
          refused === 'replaced'
            ? s.refusedReplaced
            : refused === 'expired'
              ? s.refusedExpired
              : refused === 'closed'
                ? phase === 'farewell' || state?.access.sealed
                  ? s.refusedEnded
                  : s.refusedNotOpen
                : s.offline
        }
        show={Boolean(state) && (offline || refused !== null)}
      />
    </Shell>
  );
}

/** The year an era is, for the round's cards. */
function eraYear(chapter: Chapter): string {
  return ['', '2026', '2213', '2398', '2617', '2791'][chapter] ?? '';
}

function lookAccent(chapter: Chapter, phase: string): string {
  if (phase === 'jump' || phase === 'charge') return '#a08cff';
  if (phase === 'farewell') return '#d9b96a';
  return ['#4fd8c2', '#6fb7ff', '#ff4fd8', '#ff2e3f', '#e08a3c', '#5c7d4e'][chapter] ?? '#4fd8c2';
}

function nameOf(state: NonNullable<Room['state']>, seat: number): string {
  return state.seats.find((x) => x.seat === seat)?.callsign ?? `#${String(seat).padStart(2, '0')}`;
}

/** Three real things from the evening, for the results and the card. */
function contributionsFor(state: NonNullable<Room['state']>, seat: number, s: ScreenCopy): string[] {
  const summary = eveningFor(state, seat);
  const post = state.seats.find((x) => x.seat === seat)?.profession;
  const rank = summary.rank !== null && summary.total > 0 ? s.rankOf(summary.rank, summary.players) : '';
  return [
    rank ? s.pointsLine(fmt(summary.total), rank) : s.pointsLine(fmt(summary.total), ''),
    s.gamesLine(summary.games),
    s.jobLine(post ?? '—'),
  ];
}

/**
 * This guest's role for the evening's end: a saboteur if they were one at any stop (show-core
 * `You.saboteurAt`), not only at the last; crew once the draws have begun.
 */
function eveningRole(you: You | null): Alignment | null {
  if (!you) return null;
  if (saboteurAtOf(you).length > 0) return 'PARADOX AGENT';
  return you.alignment ? 'PRESERVER' : null;
}

/** Everything the evening's story cards need, read off the room and this phone's own role. */
function wrappedInput(state: NonNullable<Room['state']>, seat: number, lang: Lang, you: You | null, chat: ChatSnapshot) {
  const summary = eveningFor(state, seat);
  // This phone's own messages, counted, once the room has sent it its conversations: never a word of them.
  const talk = chat.evening ? chatCounts(chat.messages, seat) : null;
  const sharedIn = (chapter: Parameters<typeof leaderboard>[1], rank: number | undefined) =>
    rank !== undefined && leaderboard(state, chapter).filter((row) => row.rank === rank).length > 1;
  return {
    lang,
    summary,
    outcome: state.game.outcome,
    damage: state.game.fractures,
    damageLimit: GAME.FRACTURE_LIMIT,
    uprising: state.game.uprising !== null,
    alignment: eveningRole(you),
    saboteurAt: saboteurAtOf(you),
    sharedRank: sharedIn('all', summary.rank ?? undefined),
    sharedBest: summary.bestEra ? sharedIn(summary.bestEra.chapter, summary.bestEra.rank) : false,
    // The rest of the evening, real and funny (ui/wrappedStats.ts): never a ballot.
    stats: wrappedStats(state, seat, you),
    ...(talk ? { chat: talk } : {}),
  };
}

/** Flags already set on this phone this session, for a browser that keeps no storage. */
const setFlags = new Set<string>();

function readFlag(key: string): boolean {
  if (setFlags.has(key)) return true;
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/** A yes this phone keeps under `key` (in localStorage), read afresh whenever the key changes. */
function useStoredFlag(key: string): [boolean, () => void] {
  const [version, setVersion] = useState(0);
  // `version` changes after a write, so the flag is read again.
  const on = useMemo(() => readFlag(key), [key, version]);
  const set = useCallback(() => {
    setFlags.add(key);
    try {
      localStorage.setItem(key, '1');
    } catch {
      /* private browsing: kept for this visit only */
    }
    setVersion((v) => v + 1);
  }, [key]);
  return [on, set];
}

/** Before the room answers. After ten seconds, it says what to check and who to ask. */
function Booting({ s }: { s: ScreenCopy }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setSlow(true), 10_000);
    return () => clearTimeout(id);
  }, []);
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        padding: 32,
        textAlign: 'center',
      }}
    >
      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, letterSpacing: 2, color: '#4fd8c2' }}>{s.connecting}</div>
      {slow && (
        <div style={{ maxWidth: 300, fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, lineHeight: 1.6, color: '#9db3c2' }}>
          {s.stillConnecting}
        </div>
      )}
    </div>
  );
}

function Seated({ t, s, seatNumber, callsign }: { t: { seat: string }; s: ScreenCopy; seatNumber: string; callsign: string }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        padding: 28,
        textAlign: 'center',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
      }}
    >
      {/* The seat number is the whole message here: big enough to read at arm's length. */}
      <div style={{ position: 'relative', width: 132, height: 132, flex: 'none', display: 'grid', placeItems: 'center' }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: '1px dashed rgba(79,216,194,.4)',
            animation: 'ccSway 6s ease-in-out infinite',
          }}
        />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: 3, color: '#4fd8c2' }}>{t.seat}</div>
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 54, fontWeight: 700, letterSpacing: 2, lineHeight: 1 }}>{seatNumber}</div>
        </div>
      </div>
      {callsign && <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: 3 }}>{callsign}</div>}
      <div style={{ maxWidth: 320, fontFamily: "'Rajdhani', sans-serif", fontSize: 18, fontWeight: 500, color: '#b8c6d2', lineHeight: 1.4 }}>
        {s.seated}
      </div>
    </div>
  );
}

function ToastBanner({ item, onDone }: { item: Toast; onDone: (id: number) => void }) {
  const node = (
    <TopBanner tone={{ fg: '#e6edf3', bg: '#141b24', border: 'rgba(255,255,255,.28)' }} autoHideMs={item.ms} onDismiss={() => onDone(item.id)}>
      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, lineHeight: 1.5, paddingBottom: 4 }}>{item.text}</div>
    </TopBanner>
  );
  useBanner(`toast:${item.id}`, PRIORITY.toast, node);
  return null;
}

function ConnectionBanner({ text, show }: { text: string; show: boolean }) {
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    if (!show) setHidden(false);
  }, [show]);
  const node =
    show && !hidden ? (
      <TopBanner compact tone={{ fg: '#ffd18a', bg: '#1c1405', border: 'rgba(255,181,71,.6)' }} onDismiss={() => setHidden(true)}>
        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, lineHeight: 1.45, paddingBottom: 2 }}>{text}</div>
      </TopBanner>
    ) : null;
  useBanner('offline', PRIORITY.offline, node);
  return null;
}
